import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  Button,
  TextInput,
  Text,
  Group,
  Avatar,
  ScrollArea,
  Divider,
  Loader,
} from '@mantine/core';
import { useTheme } from '../../ThemeContext';
import { PaperClipIcon, ArrowUpIcon } from '@heroicons/react/24/outline';
import { seeComments, addComments } from '../../api/api';
import { toast } from 'react-toastify';

const AddComments = ({ isOpen, setIsOpen, type, name, projectId, meetingId }) => {
  const theme = useTheme();
  const isDark = theme === 'dark';
  const fileInputRef = useRef(); // Ref for the hidden file input
  const scrollRef = useRef(); // Ref to ScrollArea for scrolling

  const [comments, setComments] = useState([]); // Store comments from the API
  const [newComment, setNewComment] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [file, setFile] = useState(null); // Store the selected file
  const [fileName, setFileName] = useState(null); // Store file name for displaying
  const user = JSON.parse(localStorage.getItem('user'));

  // Fetching comments when modal opens or when meetingId, projectId changes
  const fetchComments = async () => {
    try {
      const get = { meetingId, projectId };
      const response = await seeComments(get);
      console.log(response);
      // Extract relevant fields from the response
      const formattedComments = response.data.chat.map((comment) => ({
        message: comment.type === 'document' ? comment.fileName || '📎 File' : (comment.message || ''),
        filePath: comment.type === 'document' ? comment.message : null, // Store file path for documents
        username: comment.userId?.name || 'Unknown User', // Handle null userId
        userId: comment.userId?._id || null, // Store user ID for alignment check
        time: new Date(comment.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        isDocument: comment.type === 'document', // Flag for document type
      }));

      setComments(formattedComments); // Update state with formatted comments
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  useEffect(() => {
    if (isOpen && type && meetingId && projectId) {
      fetchComments();
    }
  }, [isOpen, meetingId, projectId, type]);

  // Handle adding a new comment (with or without file)
  const handleAddComment = async (e) => {
    e.preventDefault(); // Prevent default form submission

    if (newComment.trim() || file) {
      setIsLoading(true);

      try {


        if (file) {
          const formData = new FormData();
          formData.append('meetingId', meetingId);
          formData.append('projectId', projectId);
          formData.append('type', 'document');
          formData.append('message', file);
          formData.append('fileName', file.name);
          const res = await addComments(formData);
          // Log FormData contents (for debugging)
          for (let [key, value] of formData.entries()) {
            console.log(`${key}:`, value);
          }
          if (res) {
            toast.success('Document uploaded successfully!');
            setFile(null); // Clear the file input after upload
            setFileName(null); // Reset file name
          }
          console.log(res)
        } else {
          const data = {
            meetingId: meetingId,
            projectId: projectId,
            type: type || 'text',
            message: newComment
          }
          const res = await addComments(data);
          if (res) {
            toast.success('Comment added successfully!');
          }
        }

        setNewComment(''); // Clear the comment input field

        // Re-fetch the latest comments after adding the new one
        await fetchComments();
      } catch (error) {
        console.error('Error adding comment:', error);
        toast.error('Failed to add comment');
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Handle file upload
  const handleFileUpload = (selectedFile) => {
    setFile(selectedFile);
    setFileName(selectedFile.name);
  };

  // Handle file download
  const handleFileDownload = async (filePath) => {
    if (filePath) {
      try {
        let downloadUrl;
        const fileName = filePath.split(/[/\\]/).pop();
        
        // Files are stored in meeting folder
        if (filePath.includes('meeting')) {
          downloadUrl = `${process.env.REACT_APP_IMAGEURL}/${filePath}`;
        } else if (filePath.includes('upload_documents')) {
          downloadUrl = `${process.env.REACT_APP_IMAGEURL}/${filePath}`;
        } else {
          // Default to meeting folder
          downloadUrl = `${process.env.REACT_APP_IMAGEURL}/meeting/${meetingId}/${fileName}`;
        }
        
        console.log('Download URL:', downloadUrl);
        
        const response = await fetch(downloadUrl);
        
        if (!response.ok) {
          throw new Error('Access denied');
        }
        
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Download error:', error);
        alert('Failed to download file: ' + error.message);
      }
    }
  };

  // Ensure the chat scrolls to the latest comment
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [comments]);

  return (
    <Modal
      opened={isOpen}
      onClose={() => setIsOpen(false)}
      title={name}
      centered
      size="lg"
      styles={{
        modal: {
          backgroundColor: isDark ? '#2c2c2c' : '#ffffff',
          color: isDark ? '#ffffff' : '#000000',
        },
      }}
    >
      <form onSubmit={handleAddComment}>
        <ScrollArea
          style={{ maxHeight: 400, overflowY: 'auto' }}
          ref={scrollRef}
          styles={{
            viewport: {
              overflowY: 'auto',
            },
            scrollbar: {
              '&[data-orientation="vertical"] .mantine-ScrollArea-thumb': {
                backgroundColor: isDark ? '#3a3b3c' : '#dcdcdc',
              },
            },
          }}
        >
          <div style={{ padding: '8px' }}>
            {comments.length === 0 ? (
              <Text align="center" size="sm" style={{ color: isDark ? '#b0b0b0' : '#808080' }}>
                No new messages for you
              </Text>
            ) : (
              comments.map((comment, index) => (
                <Group
                  key={index}
                  align="flex-start"
                  style={{
                    flexDirection: comment.userId === user._id ? 'row-reverse' : 'row',
                    marginBottom: '12px',
                  }}
                >
                  <Avatar
                    radius="xl"
                    size="md"
                    style={{
                      backgroundColor: comment.userId === user._id ? '#25d366' : '#34b7f1',
                    }}
                  >
                    {comment.username ? comment.username[0] : '?'}
                  </Avatar>
                  <div
                    style={{
                      backgroundColor: comment.userId === user._id ? (isDark ? '#075e54' : '#dcf8c6') : (isDark ? '#262d31' : '#ffffff'),
                      color: isDark ? '#ffffff' : '#000000',
                      borderRadius: '16px',
                      padding: '10px 14px',
                      maxWidth: '70%',
                      boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.2)',
                    }}
                  >
                    <Text size="sm" weight={500} style={{ marginBottom: '4px' }}>
                      {comment.userId === user._id ? 'You' : comment.username}
                    </Text>
                    {comment.isDocument ? (
                      <div 
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
                        onClick={() => handleFileDownload(comment.filePath)}
                      >
                        <span style={{ fontSize: '20px' }}>📎</span>
                        <Text size="sm" style={{ color: isDark ? '#34b7f1' : '#0066cc', textDecoration: 'underline' }}>
                          {comment.message}
                        </Text>
                      </div>
                    ) : (
                      <Text size="sm">{comment.message}</Text>
                    )}
                    <Text size="xs" style={{ marginTop: '4px', color: isDark ? '#b0b0b0' : '#808080' }}>
                      {comment.time}
                    </Text>
                  </div>
                </Group>
              ))
            )}
          </div>
        </ScrollArea>
        <Divider my="md" />
        <Group align="center" spacing="xs" style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
          <TextInput
            placeholder="Type your message..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            styles={{
              input: {
                backgroundColor: isDark ? '#3a3b3c' : '#f0f0f0',
                color: isDark ? '#ffffff' : '#000000',
              },
            }}
            style={{ flexGrow: 1 }}
          />
          <Button
            type="submit"
            disabled={isLoading || (!newComment.trim() && !file)}
            styles={{
              root: {
                backgroundColor: '#25d366',
                '&:hover': { backgroundColor: '#1ebc5c' },
              },
            }}
          >
            {isLoading ? <Loader color="white" size="sm" /> : <ArrowUpIcon className="h-5 w-5 text-blue-700" />}
          </Button>
          {type === 'meetings' && (
            <div>
              <PaperClipIcon
                className="w-5 h-5 text-green-600"
                onClick={() => fileInputRef.current.click()}
              />
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf, .docx, .txt"
                className="hidden"
                onChange={(e) => handleFileUpload(e.target.files[0])}
              />
              {fileName && (
                <Text size="xs" style={{ marginLeft: '8px', color: isDark ? '#b0b0b0' : '#808080' }}>
                  {fileName}
                </Text>
              )}
            </div>
          )}
        </Group>
      </form>
    </Modal>
  );
};

export default AddComments;
